import React from 'react'
import WisdomThreadCard, { THREAD_TYPES } from './cards/WisdomThreadCard'
import StoryCard from './cards/StoryCard'

/**
 * WisdomThreadsSection Component
 *
 * Displays family wisdom threads.
 * When useNewLayout is true, renders StoryCard grid grouped by time period.
 * When false (Classic View), uses existing WisdomThreadCard behavior.
 */
function WisdomThreadsSection({
  threads = [],
  stories = [],
  useNewLayout = false,
  waitingCount = 0,
  onThreadAction,
  onStartThread,
  onViewStory,
}) {
  // Group items by time period
  const now = new Date()
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)

  if (useNewLayout) {
    // New StoryCard layout
    const thisWeek = []
    const thisMonth = []
    const older = []

    stories.forEach(story => {
      const storyDate = story.sharedAt ? new Date(story.sharedAt) : null
      if (!storyDate || storyDate >= oneWeekAgo) {
        thisWeek.push(story)
      } else if (storyDate >= oneMonthAgo) {
        thisMonth.push(story)
      } else {
        older.push(story)
      }
    })

    // Running color index across all groups
    let colorIdx = 0

    const renderStoryGroup = (groupStories) => (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {groupStories.map(story => {
          const idx = colorIdx++
          return (
            <StoryCard
              key={story.id}
              story={story}
              colorIndex={idx}
              onViewStory={onViewStory}
            />
          )
        })}
      </div>
    )

    if (stories.length === 0) {
      return (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <span className="text-4xl mb-4 block">🔍</span>
          <h3 className="font-serif font-bold text-lg text-gray-800 mb-2">
            No stories found
          </h3>
          <p className="font-serif text-gray-500 text-sm mb-4">
            Try adjusting your search or filters to find more stories
          </p>
          <button
            onClick={onStartThread}
            className="bg-fb-olive hover:bg-fb-olive/90 text-white font-serif font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            Share a Story
          </button>
        </div>
      )
    }

    return (
      <div className="space-y-8">
        {thisWeek.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-serif font-bold text-xl text-fb-olive">
                This Week
              </h2>
              <span className="bg-fb-olive/10 text-fb-olive text-xs font-semibold px-2 py-0.5 rounded-full">
                {thisWeek.length}
              </span>
            </div>
            {renderStoryGroup(thisWeek)}
          </section>
        )}

        {thisMonth.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-serif font-bold text-xl text-fb-olive">
                This Month
              </h2>
              <span className="bg-fb-olive/10 text-fb-olive text-xs font-semibold px-2 py-0.5 rounded-full">
                {thisMonth.length}
              </span>
            </div>
            {renderStoryGroup(thisMonth)}
          </section>
        )}

        {older.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4">
              <h2 className="font-serif font-bold text-xl text-fb-olive">
                Older
              </h2>
              <span className="bg-fb-olive/10 text-fb-olive text-xs font-semibold px-2 py-0.5 rounded-full">
                {older.length}
              </span>
            </div>
            {renderStoryGroup(older)}
          </section>
        )}
      </div>
    )
  }

  // Classic layout (existing behavior)
  const thisWeek = []
  const thisMonth = []
  const older = []

  threads.forEach(thread => {
    const threadDate = thread.rawDate ? new Date(thread.rawDate) : null
    if (!threadDate || threadDate >= oneWeekAgo) {
      thisWeek.push(thread)
    } else if (threadDate >= oneMonthAgo) {
      thisMonth.push(thread)
    } else {
      older.push(thread)
    }
  })

  const renderGroup = (groupThreads) => (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
      {groupThreads.map(thread => (
        <WisdomThreadCard
          key={thread.id}
          thread={thread}
          isWaiting={thread.isWaiting}
          onAction={onThreadAction}
        />
      ))}
    </div>
  )

  if (threads.length === 0) {
    return (
      <div className="space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <span className="text-4xl mb-4 block">🌱</span>
          <h3 className="font-serif font-bold text-lg text-gray-800 mb-2">
            No threads yet
          </h3>
          <p className="font-serif text-gray-500 text-sm mb-4">
            Start a new thread to share wisdom with your family
          </p>
          <button
            onClick={onStartThread}
            className="bg-fb-olive hover:bg-fb-olive/90 text-white font-serif font-semibold px-6 py-2 rounded-lg transition-colors"
          >
            Start a Thread
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {thisWeek.length > 0 && (
        <section>
          <h2 className="font-serif font-bold text-xl text-fb-olive mb-4">
            This Week's Activity
          </h2>
          {renderGroup(thisWeek)}
        </section>
      )}

      {thisMonth.length > 0 && (
        <section>
          <h2 className="font-serif font-bold text-xl text-fb-olive mb-4">
            This Month's Activity
          </h2>
          {renderGroup(thisMonth)}
        </section>
      )}

      {older.length > 0 && (
        <section>
          <h2 className="font-serif font-bold text-xl text-fb-olive mb-4">
            Older
          </h2>
          {renderGroup(older)}
        </section>
      )}
    </div>
  )
}

/**
 * StartNewThreadSection - Bottom action bar for creating threads
 */
function StartNewThreadSection({ onStartThread }) {
  const threadOptions = [
    { type: 'photo_voice', label: 'Photo + Voice', color: 'bg-fb-olive' },
    { type: 'video_wisdom', label: 'Video Wisdom', color: 'bg-thread-video' },
    { type: 'then_now', label: 'Then & Now', color: 'bg-thread-compare' },
    { type: 'artifact', label: 'Family Artifact', color: 'bg-thread-artifact' },
    { type: 'memory_layers', label: 'Memory Layers', color: 'bg-fb-olive' },
    { type: 'time_capsule', label: 'Time Capsule', color: 'bg-thread-capsule' },
  ]

  return (
    <div className="bg-white rounded-xl border-2 border-dashed border-fb-olive p-6">
      <div className="text-center mb-4">
        <h3 className="font-serif font-bold text-lg text-gray-900">
          + Start a New Thread
        </h3>
        <p className="font-serif text-gray-500 text-sm">
          Choose a way to share wisdom with your family
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-3">
        {threadOptions.map(option => (
          <button
            key={option.type}
            onClick={() => onStartThread && onStartThread(option.type)}
            className={`
              ${option.color} hover:opacity-90
              text-white font-serif font-semibold text-sm
              px-4 py-2 rounded-lg transition-all duration-200
              flex items-center gap-2
            `}
          >
            <span>{THREAD_TYPES[option.type]?.icon}</span>
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

export default WisdomThreadsSection
